import {
    Box,
    Flex,
    Input,
    Select,
    SimpleGrid,
    Text,
    useToast,
} from '@chakra-ui/react';
import {
    DashboardPageContent,
    FiltersWrapper,
    FormWrapper,
    PageTitle,
} from 'custom-style-components';
import { EventType, People } from 'types/models';
import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import ConfirmDeleteComponent from 'components/ConfirmDeleteComponent';
import CustomButton from 'components/Button';
import ErrorMessage from 'components/ErrorMessage';
import EventsTable from 'components/EventTable';
import { GenericFrontError } from 'errors/GenericFrontError';
import LoadingSpinner from 'components/LoadingSpinner';
import { ResponseError } from 'errors/ResponseError';
import ShowParticipatingUsers from 'pages/Dashboard/Organizer/components/ShowParticipatingUsers';
import TOAST from 'constants/TOAST';
import { api } from 'services/axios';
import createCancelTokenSource from 'utils/createCancelTokenSource';
import { fetchManyOldEvents } from 'services/fetch/events';
import { fetchManyUsersFromEvent } from 'services/fetch/users';
import { useRouteMatch } from 'react-router-dom';

const DEBOUNCE_MS = 400;

const ListOldEvents: React.FC = () => {
    const toast = useToast({
        duration: TOAST.DURATION,
        position: TOAST.DEFAULT_POSITION,
        isClosable: TOAST.CLOSABLE_BY_DEFAULT,
    });

    const [startYear, setStartYear] = useState<number>();
    const [eventListYear, setEventYears] = useState<number[]>();

    const [oldEventName, setOldEventName] = useState('');
    const [debouncedOldEventName, setDebouncedOldEventName] = useState('');

    const [oldEventData, setOldEventData] = useState({} as EventType);
    const [oldEventList, setOldEventList] = useState<EventType[]>([]);

    const [modalOpen, setModalOpen] = useState(false);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>();

    const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false);
    const [idToDelete, setIdToDelete] = useState(-1);

    const { path } = useRouteMatch();

    const source = useRef(createCancelTokenSource());
    const yearDebounceRef = useRef<ReturnType<typeof setTimeout>>();

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedOldEventName(oldEventName);
        }, DEBOUNCE_MS);
        return () => clearTimeout(handler);
    }, [oldEventName]);

    const filteredEvents = useMemo(() => {
        if (!debouncedOldEventName) return oldEventList;
        const lower = debouncedOldEventName.toLowerCase();
        return oldEventList.filter((event) => {
            const category = event.eventCategory?.category?.toLowerCase() ?? '';
            const sigla = event.eventArea?.sigla?.toLowerCase() ?? '';
            return category.includes(lower) || sigla.includes(lower);
        });
    }, [debouncedOldEventName, oldEventList]);

    // Carregamento de eventos 
    const loadEvents = useCallback(() => {
        setLoading(true);
        source.current = createCancelTokenSource();

        fetchManyOldEvents(source.current.token, {
            startYear,
            limit: Number.MAX_SAFE_INTEGER,
            all: true,
        })
            .then(({ oldEvents }) => {
                setOldEventList(oldEvents);

                if (startYear === undefined) {
                    const years = Array.from(
                        new Set(oldEvents.map((e) => e.startDate.getFullYear()))
                    );
                    setEventYears(years);
                }
            })
            .catch((err) => {
                setError(err.message);
                toast({ title: err.message, status: 'error' });
            })
            .finally(() => setLoading(false));

        return () => source.current.cancel();
    }, [startYear, toast]);

    useEffect(() => {
        const cancel = loadEvents();
        return cancel;
    }, [loadEvents]);

    const handleParticipantsClick = useCallback((event: EventType) => {
        setOldEventData(event);
        setModalOpen(true);
    }, []);

    const handleDeleteClick = useCallback((id: number) => {
        setIdToDelete(id);
        setConfirmDeleteModalOpen(true);
    }, []);

    const deleteEvent = async (id: number) => {
        try {
            await api.delete(`/sge/${id}`);
            // Atualiza o estado local sem recarregar todos os eventos da API
            setOldEventList((prev) => prev.filter((ev) => ev.id !== id));
            toast({
                title: 'Edição excluída',
                description: `Exclusão da edição ${id}`,
                status: 'success',
            });
        } catch (err) {
            let message = 'Erro';
            if (err instanceof GenericFrontError) {
                message = err.message;
                if (err instanceof ResponseError && err.status === 404)
                    message = 'Edição não encontrada';
            }
            toast({
                title: 'Erro na exclusão',
                description: message,
                status: 'error',
            });
        } finally {
            setConfirmDeleteModalOpen(false);
        }
    };

    const changeYear = useCallback((ev: ChangeEvent<HTMLSelectElement>): void => {
        if (ev.target.value !== undefined) {
            setLoading(true);
            if (yearDebounceRef.current) clearTimeout(yearDebounceRef.current);
            yearDebounceRef.current = setTimeout(() => {
                const year = ev.target.value;
                setStartYear(year === '' ? undefined : parseInt(year));
            }, 200);
        }
    }, []);

    return (
        <DashboardPageContent>
            <ShowParticipatingUsers
                event={oldEventData}
                modalOpen={modalOpen}
                setModalOpen={setModalOpen}
            />
            <FormWrapper>
                <ConfirmDeleteComponent
                    modalOpen={confirmDeleteModalOpen}
                    setModalOpen={setConfirmDeleteModalOpen}
                    handleDelete={() => deleteEvent(idToDelete)}
                />

                <PageTitle>Edições anteriores</PageTitle>
                <CustomButton link={'edicoes/cadastrar'}>
                    Cadastrar edição
                </CustomButton>

                {oldEventList.length > 0 && (
                    <FiltersWrapper>
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} w="100%" mt={8} mb={6}>
                            <Box>
                                <Text fontSize="xl" fontWeight="bold" mb={2} color="gray.700">
                                    Ano de início
                                </Text>
                                <Select
                                    bg="white"
                                    size="lg"
                                    fontSize="xl"
                                    h="50px"
                                    placeholder="Qualquer ano"
                                    onChange={changeYear}
                                    focusBorderColor="blue.400"
                                    shadow="sm"
                                    borderRadius="md"
                                >
                                    {eventListYear && eventListYear.length > 0 ? (
                                        eventListYear.map((y) => (
                                            <option key={y} value={y}>{y}</option>
                                        ))
                                    ) : (
                                        <option disabled>Sem Opções</option>
                                    )}
                                </Select>
                            </Box>

                            <Box>
                                <Text fontSize="xl" fontWeight="bold" mb={2} color="gray.700">
                                    Pesquisar Edições Anteriores
                                </Text>
                                
                                <Input
                                    size="lg"
                                    fontSize="xl"
                                    h="50px"
                                    bg="white"
                                    type="text"
                                    value={oldEventName}
                                    onChange={(e) => setOldEventName(e.target.value)}
                                    placeholder="Pesquise por nome ou sigla da área"
                                    focusBorderColor="blue.400"
                                    shadow="sm"
                                    borderRadius="md"
                                />
                            </Box>
                        </SimpleGrid>
                    </FiltersWrapper>
                )}

                {error ? (
                    <ErrorMessage>{error}</ErrorMessage>
                ) : loading ? (
                    <Flex justify="center" p={10}>
                        <LoadingSpinner />
                    </Flex>
                ) : (
                    <EventsTable
                        events={filteredEvents}
                        path={path}
                        onDeleteClick={handleDeleteClick}
                        onParticipantsClick={handleParticipantsClick}
                    />
                )}
            </FormWrapper>
        </DashboardPageContent>
    );
};

export default ListOldEvents;
